import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CategoryService } from '../../osm-services/category.service';
import { UserFavAppService } from '../../osm-services/user-fav-app.service';
import { AuthService } from '../../services/auth.service';
import { ImageUtils } from '../../shared/utils/image.utils';

interface App {
  appliId: string;
  name: string;
  icon: string;
  androidImageUrl: string;
  link: string;
  fallbackUrl: string;
  category: string;
  categoryId?: string;
  categoryName?: string;
  subCategory: string;
  subCategoryId?: string;
  subCategoryName?: string;
  active: boolean;
  order: number;
}

@Component({
  selector: 'app-select-favorite',
  templateUrl: './select-favorite-app.component.html',
  styleUrls: ['./select-favorite-app.component.scss']
})
export class SelectFavoriteAppComponent implements OnInit, OnDestroy {
  subCategoryId: string = '';
  subCategoryName: string = '';
  categoryId: string = '';
  categoryName: string = '';
  
  apps: App[] = [];
  filteredApps: App[] = [];
  searchTerm: string = '';
  
  loading = false;
  saving = false;
  error: string | null = null;
  userId: string = '';
  
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private categoryService: CategoryService,
    private userFavAppService: UserFavAppService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Get user ID
    this.authService.getUser().then(user => {
      if (user && user.attributes) {
        this.userId = user.attributes.sub;
      }
    });

    // Get subcategory ID from route
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.subCategoryId = params['subCategoryId'];
      });

    // Get query parameters
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.subCategoryName = params['subcategoryName'] || '';
        this.categoryId = params['categoryId'] || '';
        this.categoryName = params['categoryName'] || '';
        
        if (this.subCategoryId) {
          this.loadApps();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadApps(): void {
    this.loading = true;
    this.error = null;

    this.categoryService.getAppsBySubcategory(this.subCategoryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.apps = response.items || [];
          this.filteredApps = [...this.apps];
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading apps:', error);
          this.error = 'Failed to load apps';
          this.loading = false;
        }
      });
  }

  onSearchChange(): void {
    const term = this.searchTerm.toLowerCase().trim();
    
    if (!term) {
      this.filteredApps = [...this.apps];
    } else {
      this.filteredApps = this.apps.filter(app =>
        app.name.toLowerCase().includes(term)
      );
    }
  }

  selectApp(app: App): void {
    if (!this.userId) {
      alert('Please log in to select a favorite app');
      return;
    }

    if (confirm(`Set "${app.name}" as your favorite for ${this.subCategoryName}?`)) {
      this.saving = true;

      const favoriteData = {
        appliId: app.appliId,
        appName: app.name,
        subCategoryId: this.subCategoryId,
        subCategoryName: this.subCategoryName,
        categoryId: this.categoryId,
        categoryName: this.categoryName,
        icon: app.icon || app.androidImageUrl,
        link: app.link || app.fallbackUrl,
        order: app.order
      };

      this.userFavAppService.setFavoriteApp(this.userId, favoriteData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.saving = false;
            // Navigate back to category detail page
            this.router.navigate(['/category-detail', this.categoryId]);
          },
          error: (error) => {
            console.error('Error saving favorite:', error);
            alert('Failed to save favorite app');
            this.saving = false;
          }
        });
    }
  }

  goBack(): void {
    this.router.navigate(['/category-detail', this.categoryId]);
  }

  getAppIcon(app: App): string {
    return ImageUtils.getAppIcon(app);
  }

  handleImageError(event: Event): void {
    ImageUtils.handleImageError(event);
  }
}
